import React, { memo, useCallback, useMemo } from 'react'
import { IEntityCompanySettingsHeaderProps } from 'src/models'
import Grid from '@material-ui/core/Grid'
import { makeStyles } from '@material-ui/core/styles'
import { Trans, useTranslation } from 'react-i18next'
import Button from '@material-ui/core/Button'
import { KeyboardArrowLeft } from '@material-ui/icons'
import { useHistory, useParams } from 'react-router-dom'
import Avatar from '@material-ui/core/Avatar'
import classNames from 'classnames'
import useMediaQuery from '@material-ui/core/useMediaQuery'

import { StoreValue } from 'store'
import DeleteCompany from './deleteCompany'
import styles from './styles'
import { buildCompanyLogo } from 'utils'
import { BreadcrumbsView } from 'screens/company/components'
import { ECompanySettingsTabs } from 'models'
import { SectionsLists } from './'

const useStyles = makeStyles(styles)

const EntityCompanySettingsHeaderView: React.FunctionComponent<IEntityCompanySettingsHeaderProps> = ({
	id,
	logoPath,
	description,
	tabsObj,
	toggleTemp,
	currentTabIndex,
	hasCurrentUserAdminPermission,
	hasCurrentUserManagerPermission,
	hasCurrentUserPublisherPermission,
	storeUserIg,
	storeUserOwnedCompanies,
	name,
}) => {
	const isMobile = useMediaQuery('@media (max-width:650px)')
	const { companySlug }: { companySlug?: string } = useParams()
	const classes = useStyles()
	const { t } = useTranslation()
	const history = useHistory()
	const setTab = useCallback(
		(key: string) => () => {
			toggleTemp(key)
		},
		[toggleTemp],
	)

	const handleGoBack = useCallback(() => {
		history.goBack()
	}, [history])

	const canDelete = useMemo(() => {
		const checkCompanies: {
			id: string
			name: string
			slug: string
		}[] = storeUserOwnedCompanies
			? storeUserOwnedCompanies?.filter(
					(item: { id: string; name: string; slug: string }) => {
						return item.slug === companySlug
					},
			  )
			: []
		if (checkCompanies.length > 0 || storeUserIg) {
			return true
		}
	}, [companySlug, storeUserOwnedCompanies, storeUserIg])

	return (
		<Grid
			className={classes.companySettingsHeader}
			container={true}
			direction="column"
		>
			<Grid
				className={classes.entityHeaderContent}
				container={true}
				direction="column"
				spacing={2}
			>
				<Grid item={true} className={classes.detailsSection}>
					<div className={classes.backBtnBreadcrumbsContainer}>
						<Button
							className={classes.backBtn}
							onClick={() => handleGoBack()}
						>
							<KeyboardArrowLeft />
							<span className={classes.backBtnText}>
								{t('Back')}
							</span>
						</Button>
						<div className={classes.breadcrumbsContainer}>
							<BreadcrumbsView
								companyName={name}
								section={tabsObj[currentTabIndex]}
								companySlug={companySlug}
							/>
						</div>
					</div>
					<div className={classes.logoContainer}>
						<div className={classes.logoBox}>
							<Avatar
								classes={{ root: classes.avatar }}
								src={buildCompanyLogo(
									id || '',
									logoPath || 'company-logo-original.jpg',
								)}
							></Avatar>
						</div>
						<div className={classes.titleSection}>
							<span className={classes.title}>
								<Trans i18nKey="companySettings">
									<strong>Settings</strong>.
								</Trans>
							</span>
						</div>
					</div>
					<div
						className={classes.subTitle}
						dangerouslySetInnerHTML={{
							__html: description,
						}}
					/>
				</Grid>
				{!isMobile ? (
					<Grid item={true}>
						<Grid container={true} direction="row">
							{Object.keys(tabsObj)
								.filter(item => {
									if (
										storeUserIg ||
										hasCurrentUserAdminPermission
									) {
										return true
									} else if (
										hasCurrentUserManagerPermission &&
										item !==
											ECompanySettingsTabs.UserAccounts
									) {
										return true
									} else if (
										hasCurrentUserPublisherPermission &&
										item === ECompanySettingsTabs.Jobs
									) {
										return true
									} else {
										return false
									}
								})
								.map((tab: any, index: number) => {
									return (
										<Grid
											key={`tab-${index}`}
											item={true}
											onClick={setTab(tab)}
											className={classNames(
												classes.tabLabelContainer,
												currentTabIndex === tab
													? classes.selectedTabLabelContainer
													: '',
											)}
										>
											<span>
												{
													// @ts-ignore
													tabsObj[tab]
												}
											</span>
										</Grid>
									)
								})}
							{canDelete && (
								<Grid
									item={true}
									className={classes.deleteButton}
								>
									<DeleteCompany id={id} />
								</Grid>
							)}
						</Grid>
					</Grid>
				) : (
					<Grid item={true}>
						<SectionsLists
							tabsObj={tabsObj}
							hasCurrentUserAdminPermission={
								hasCurrentUserAdminPermission
							}
							hasCurrentUserManagerPermission={
								hasCurrentUserManagerPermission
							}
							hasCurrentUserPublisherPermission={
								hasCurrentUserPublisherPermission
							}
							storeUserIg={storeUserIg}
							setTab={setTab}
							currentTabIndex={currentTabIndex}
						/>
					</Grid>
				)}
			</Grid>
		</Grid>
	)
}

const EntityCompanySettingsHeader = (
	props: IEntityCompanySettingsHeaderProps,
) => (
	<StoreValue keys={['user.profile.ownedCompanies|storeUserOwnedCompanies']}>
		<EntityCompanySettingsHeaderView {...props} />
	</StoreValue>
)

export default memo(EntityCompanySettingsHeader)
